import { isLikelyToFillProfession, type TLikelyToFillBands } from './likelyToFillConfig';

/** `unlikely` is the red band, below `warning`. */
export type TLikelyToFillBand = 'veryLikely' | 'likely' | 'lessLikely' | 'warning' | 'unlikely';

// Same names as the tones the pending @tempmee/react `RateSelection` `indicator` prop takes.
export type TLikelyToFillTone = 'positive' | 'neutral' | 'warning' | 'negative';

export interface ILikelyToFillIndicator {
  readonly band: TLikelyToFillBand;
  readonly label: string;
  readonly tone: TLikelyToFillTone;
  /** The alert sentence; only the amber (`warning`) and red (`unlikely`) bands have one. */
  readonly message?: string;
}

const TONE: Record<TLikelyToFillBand, TLikelyToFillTone> = {
  veryLikely: 'positive',
  likely: 'positive',
  lessLikely: 'neutral',
  warning: 'warning',
  unlikely: 'negative',
};

// Figma wording, verbatim (case and punctuation differ between bands on purpose).
const LABEL: Record<TLikelyToFillBand, string> = {
  veryLikely: 'Very Likely to Fill',
  likely: 'Likely to fill.',
  lessLikely: 'Less likely to fill',
  warning: 'Less likely to fill',
  unlikely: 'Unlikely to fill',
};

// Figma wording, same for every profession; design may still change it.
type TAlertBand = Extract<TLikelyToFillBand, 'warning' | 'unlikely'>;

const ALERT_COPY: Record<TAlertBand, (amount: string) => string> = {
  warning: (amount) => `This rate is ${amount} below market, so fewer professionals will apply.`,
  unlikely: (amount) =>
    `At ${amount} below market, this shift will rarely get professionals to apply.`,
};

// The first whole-dollar shortfall below a band's lowest gap, e.g. lessLikely −3 → "$4+".
const shortfallBelow = (lowestGap: number): string => `$${1 - lowestGap}+`;

// The gap is against the per-office average, never the GOTU-972 enhanced rate.
export const getLikelyToFillBand = (
  rate: number,
  averageRate: number,
  professionCode: string | undefined,
  bands: TLikelyToFillBands,
): ILikelyToFillIndicator | null => {
  if (!isLikelyToFillProfession(professionCode)) return null;
  if (!Number.isFinite(rate) || !Number.isFinite(averageRate) || averageRate <= 0) {
    return null;
  }

  const limits = bands[professionCode];
  const gap = rate - Math.round(averageRate);

  const toIndicator = (band: TLikelyToFillBand): ILikelyToFillIndicator => ({
    band,
    label: LABEL[band],
    tone: TONE[band],
  });

  if (gap >= limits.veryLikely) return toIndicator('veryLikely');
  if (gap >= limits.likely) return toIndicator('likely');
  if (gap >= limits.lessLikely) return toIndicator('lessLikely');
  if (gap >= limits.warning) {
    return {
      ...toIndicator('warning'),
      message: ALERT_COPY.warning(shortfallBelow(limits.lessLikely)),
    };
  }
  return {
    ...toIndicator('unlikely'),
    message: ALERT_COPY.unlikely(shortfallBelow(limits.warning)),
  };
};
